<script setup>

    import { onMounted } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useStorage } from '@vueuse/core'
    import { useAuthStore, useClientStore, useStaffStore } from '@/Stores'

    import LatteStyledContainer from '@/Components/App/LatteStyledContainer.vue'
    import LatteSalesList from '@/Components/LatteSalesList.vue'

    import StaffDropdown from '@/Components/Forms/StaffDropdown.vue'

    import { ArrowPathIcon } from '@heroicons/vue/20/solid'

    const clientStore = useClientStore()
    const staffStore = useStaffStore()
    const authStore = useAuthStore()
    const { user } = storeToRefs(authStore)

    const { dataCompletionLoading, dataCompletionProgress, dataCompletionUserContext } = storeToRefs(clientStore)

    const staffContext = useStorage('staffContext', dataCompletionUserContext.value)

    function handleStaffContextChange(newStaffUser) {
        staffContext.value = newStaffUser
        clientStore.dataCompletionUserContext = newStaffUser
    }

    onMounted(async () => {
        staffStore.getAllStaffs()
            .then(response => {
                    rehydrateStaffs(response.data)
                })
    })

    async function hydrateStaffs(data) {
        await staffStore.hydrateAllStaffs(data)
        // loading.value = false
    }

    function rehydrateStaffs(data) {
        // loading.value = true
        hydrateStaffs(data)
    }

</script>

<template>

    <div class="mx-auto sm:pt-4 md:pt-6">

        <div class="mx-auto max-w-5xl px-4 grid grid-cols-1 gap-4 md:grid-cols-12">

            <div class="sm:col-span-1 pt-4 pb-6 md:py-0 md:col-span-6 md:pr-12 flex flex-col gap-y-4">

                <h3 class="font-bold text-lg text-lifeworx-blue-700">
                    Welcome: Data Completion Initiative
                </h3>

                <p class="text-sm md:text-sm tracking-wide">
                    This is a purpose-built starting point for completing our latest data accuracy goals to support marketing and sales efforts.
                </p>
                <p class="text-sm md:text-sm tracking-wide">
                    Set the profile <b>Disposition</b> first for the most relevant completion requirements</p>

            </div>


            <div class="sm:col-span-1 md:col-span-6 md:-mt-2">

                <h5 class="text-lifeworx-blue-600 font-semibold text-sm tracking-wide my-2">
                    Tips &amp; Best Practices
                </h5>

                <ul class="list-disc list-outside pl-4 list text-sm md:text-sm tracking-wide space-y-1">
                    <li>Set the profile <b>Disposition</b> first for the most relevant completion requirements</li>
                    <li>Review missing fields by hovering over each of the four data category icons</li>
                    <li>Ctrl+Click (or ⌘+Click on Mac) to open a profile in a new tab. Right click for quick access to the Coffee Profile and Notes</li>

                </ul>

            </div>

        </div>

        <template v-if="user && user.is_sale_manager">

            <LatteStyledContainer
                id="sales-datatable"
                title="Client Data Completion for"
                :forceSingleLineMobileLayout="true"
                :progress="dataCompletionProgress"
                :supports="[ 'widescreen' ]"
                :centerHeaderSlot="false"
            >

                <LatteSalesList />

                <template #header>
                    <div class="w-fit min-w-0 md:-ml-4">
                        <StaffDropdown
                            :staff="staffContext"
                            @assigned-staff:update="handleStaffContextChange"
                            variant="container"
                            filter="activeStaffsWithClients"
                            :showClearButton="true"
                        />
                    </div>
                </template>

                <template #header-actions>
                    <button
                        @click="clientStore.refreshDataCompletionList()"
                        class="h-10 w-10 mt-0.5 mr-2 md:h-7 md:w-7 p-1 grow-0 justify-self-end items-center justify-center rounded-md text-white hover:bg-lifeworx-blue-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-lifeworx-blue-700"
                    >
                        <span class="sr-only">Refresh</span>
                        <ArrowPathIcon :class="[ 'block h-5 w-5', { 'animate-spin': dataCompletionLoading } ]" aria-hidden="true" />
                    </button>
                </template>

            </LatteStyledContainer>

        </template>
    </div>

</template>
